import React, { useState, useEffect } from 'react';
import api from '../../api/auth';

const GradeEntry = () => {
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [selectedEval, setSelectedEval] = useState(null);
  const [students, setStudents] = useState([]);
  const [gradesData, setGradesData] = useState({});
  const [saving, setSaving] = useState(false);
  const [evalInfo, setEvalInfo] = useState(null);

  useEffect(() => {
    fetchEvaluations();
  }, []);

  const fetchEvaluations = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await api.get('/teacher/evaluations', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvaluations(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const openGradeModal = async (evaluation) => {
    try {
      setSelectedEval(evaluation);
      setShowModal(true);
      setStudents([]);
      setGradesData({});
      setEvalInfo(null);

      const token = localStorage.getItem('token');
      const res = await api.get(`/teacher/evaluations/${evaluation.id}/students`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const list = res.data.students || [];
      setStudents(list);
      setEvalInfo(res.data.evaluation);

      // Pré-remplir les notes existantes
      const initial = {};
      list.forEach((s) => {
        initial[s.id] = {
          score: s.score ?? '',
          appreciation: s.appreciation ?? '',
        };
      });
      setGradesData(initial);
    } catch (err) {
      console.error('Erreur:', err);
      alert('Erreur lors du chargement des élèves');
    }
  };

  const handleGradeChange = (studentId, field, value) => {
    setGradesData((prev) => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        [field]: value,
      },
    }));
  };

  const handleSave = async (status) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');

      const grades = Object.entries(gradesData)
        .filter(([_, g]) => g.score !== '' && g.score !== null && g.score !== undefined)
        .map(([studentId, g]) => ({
          student_id: parseInt(studentId),
          score: parseFloat(g.score),
          appreciation: g.appreciation || null,
        }));

      if (grades.length === 0) {
        alert('Veuillez saisir au moins une note');
        setSaving(false);
        return;
      }

      await api.post('/teacher/grades', {
        evaluation_id: selectedEval.id,
        grades,
        status,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert(status === 'submitted'
        ? '✅ Notes soumises à l\'administration'
        : '💾 Notes enregistrées en brouillon'
      );

      setShowModal(false);
      setSelectedEval(null);
      fetchEvaluations();
    } catch (err) {
      console.error('Erreur:', err);
      alert(err.response?.data?.error || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const filteredEvaluations = evaluations.filter((ev) => {
    if (searchTerm) {
      const t = searchTerm.toLowerCase();
      if (
        !(ev.title || '').toLowerCase().includes(t) &&
        !(ev.class_name || '').toLowerCase().includes(t) &&
        !(ev.subject_name || '').toLowerCase().includes(t)
      ) return false;
    }
    if (filterType !== 'all' && ev.type !== filterType) return false;
    return true;
  });

  const getTypeBadge = (type) => {
    switch (type) {
      case 'devoir':
        return <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">📝 Devoir</span>;
      case 'interrogation':
        return <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">❓ Interrogation</span>;
      case 'composition':
        return <span className="text-xs bg-orange-100 text-[#E67E22] px-2 py-1 rounded-full">🧪 Composition</span>;
      default:
        return <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">{type}</span>;
    }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleDateString('fr-FR'); } catch { return d; }
  };

  const countFilled = () => {
    return Object.values(gradesData).filter(g => g.score !== '' && g.score !== null && g.score !== undefined).length;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#2C3E50]">✏️ Saisie des notes</h1>
        <p className="text-gray-500">Saisissez et soumettez les notes de vos évaluations</p>
      </div>

      {/* Info */}
      <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4">
        <p className="text-sm text-blue-700">
          ℹ️ <strong>Information :</strong> Une fois soumises, les notes sont envoyées à l'administration
          pour validation. Vous ne pourrez plus les modifier.
        </p>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-xl shadow-md p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input
            type="text"
            placeholder="🔍 Rechercher (titre, classe, matière)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
          />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
          >
            <option value="all">Tous les types</option>
            <option value="devoir">📝 Devoirs</option>
            <option value="interrogation">❓ Interrogations</option>
            <option value="composition">🧪 Compositions</option>
          </select>
          <div className="text-sm text-gray-500 flex items-center px-2">
            {filteredEvaluations.length} évaluation(s)
          </div>
        </div>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="bg-white rounded-xl shadow-md p-8 text-center">
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-gray-200 rounded w-1/4 mx-auto"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
          </div>
        </div>
      ) : filteredEvaluations.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <p className="text-4xl mb-3">📭</p>
          <p className="text-lg font-medium text-gray-700 mb-2">Aucune évaluation</p>
          <p className="text-sm text-gray-500">
            {evaluations.length === 0
              ? 'Vous n\'avez aucune évaluation à noter pour le moment.'
              : 'Aucun résultat pour ces filtres.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredEvaluations.map((ev) => (
            <div key={ev.id} className="bg-white rounded-xl shadow-md overflow-hidden border-l-4 border-[#E67E22]">
              <div className="p-5">
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h3 className="text-lg font-bold text-[#2C3E50]">{ev.title}</h3>
                      {getTypeBadge(ev.type)}
                      {ev.status === 'published' && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">🟢 Publiée</span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm text-gray-600">
                      <div>🏫 <strong>{ev.class_name}</strong></div>
                      <div>📚 <strong>{ev.subject_name}</strong></div>
                      <div>📅 <strong>{formatDate(ev.date)}</strong></div>
                      <div>⚖️ Coef : <strong>{ev.coefficient}</strong> / Note max : <strong>{ev.max_score}</strong></div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 items-end">
                    <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                      {ev.grades_count || 0} note(s) saisie(s)
                    </span>
                    <button
                      onClick={() => openGradeModal(ev)}
                      className="text-xs bg-[#E67E22] text-white px-4 py-2 rounded-lg hover:bg-[#D35400] transition font-medium whitespace-nowrap"
                    >
                      ✏️ Saisir les notes
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal saisie notes */}
      {showModal && selectedEval && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl p-6 w-full max-w-3xl my-8">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-[#2C3E50]">✏️ Saisie des notes</h2>
                <p className="text-sm text-gray-500 mt-1">
                  {evalInfo?.title} — {evalInfo?.class_name} — {evalInfo?.subject_name}
                </p>
                {evalInfo && (
                  <p className="text-xs text-gray-500 mt-1">
                    Note max : <strong>{evalInfo.max_score}</strong> • Coefficient : <strong>{evalInfo.coefficient}</strong>
                  </p>
                )}
              </div>
              <button
                onClick={() => { setShowModal(false); setSelectedEval(null); }}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            {students.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Chargement des élèves...
              </div>
            ) : (
              <>
                <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                  <table className="w-full">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Élève</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 w-32">Note /{evalInfo?.max_score || 20}</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Appréciation</th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.map((s) => (
                        <tr key={s.id} className="border-t hover:bg-gray-50">
                          <td className="py-2 px-4 font-medium">
                            {s.name}
                            {s.matricule && (
                              <span className="text-xs text-gray-500 block">{s.matricule}</span>
                            )}
                          </td>
                          <td className="py-2 px-4">
                            <input
                              type="number"
                              step="0.5"
                              min="0"
                              max={evalInfo?.max_score || 20}
                              value={gradesData[s.id]?.score ?? ''}
                              onChange={(e) => handleGradeChange(s.id, 'score', e.target.value)}
                              placeholder="--"
                              className="w-24 px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                            />
                          </td>
                          <td className="py-2 px-4">
                            <input
                              type="text"
                              value={gradesData[s.id]?.appreciation ?? ''}
                              onChange={(e) => handleGradeChange(s.id, 'appreciation', e.target.value)}
                              placeholder="Ex: Bien"
                              className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="mt-4 flex items-center justify-between">
                  <p className="text-sm text-gray-500">
                    <strong>{countFilled()}</strong> / {students.length} élève(s) noté(s)
                  </p>
                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={() => handleSave('draft')}
                      disabled={saving}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium disabled:opacity-50"
                    >
                      💾 Brouillon
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSave('submitted')}
                      disabled={saving}
                      className="px-4 py-2 bg-[#E67E22] text-white rounded-lg hover:bg-[#D35400] transition font-medium disabled:opacity-50"
                    >
                      📤 {saving ? 'Envoi...' : 'Soumettre à l\'admin'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GradeEntry;